import { formatParisDateTime, type FulfillmentLine } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { RotateCcw, Search, XCircle } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { Page, PageHeader } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { useOriginFilter } from '@/lib/origin-filter.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchFulfillmentErrorLines } from './api.js';
import { IgnoreReasonDialog } from './ignore-reason-dialog.js';
import { useIgnoreFulfillmentLines, useRetryFulfillmentLines } from './mutations.js';
import { RUNS_BACK_LINK } from './run-detail-layout.js';

const helper = createDataTableColumnHelper<FulfillmentLine>();

/**
 * `02` §11 « Mises à jour Martelet non appliquées » (lignes `OC1` en erreur) — page dédiée, tous runs confondus, atteinte
 * depuis l'onglet Runs et depuis les liens d'alerte
 * (`fulfillment.line_failed`). Le réessai groupé porte sur les lignes
 * affichées à l'écran, en complément du réessai unitaire par ligne.
 */
export function ErrorLinesPage(): JSX.Element {
  const { page, sortBy, sortDir, setPage, setSort, setFilters } = useDataTableUrlState();
  const { origin: store } = useOriginFilter();
  const rawSearch = useSearch({ strict: false });
  const search = typeof rawSearch.search === 'string' && rawSearch.search !== '' ? rawSearch.search : undefined;
  const [searchDraft, setSearchDraft] = useState(search ?? '');
  // `null` : rien ; une liste : les lignes à ignorer (une seule, ou toutes celles affichées).
  const [ignoreTargets, setIgnoreTargets] = useState<FulfillmentLine[] | null>(null);

  const retryMutation = useRetryFulfillmentLines();
  const ignoreMutation = useIgnoreFulfillmentLines();

  const params = {
    page,
    pageSize: 50,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
    ...(store ? { store } : {}),
    ...(search ? { search } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.runs.errorLines(params),
    queryFn: () => fetchFulfillmentErrorLines(params),
  });

  const columns = helper.columns([
    helper.accessor('fileName', {
      header: 'Fichier',
      cell: (info) => <span className="whitespace-nowrap">{info.getValue() ?? '—'}</span>,
    }),
    helper.accessor('orderNumber', {
      header: 'Commande',
      cell: (info) => (
        <span className="whitespace-nowrap font-medium tabular-nums">{info.getValue() ?? '—'}</span>
      ),
    }),
    helper.accessor('store', { header: 'Origine', cell: (info) => info.getValue() ?? '—' }),
    helper.accessor('nextRetryRunAt', {
      header: 'Nouvel essai prévu',
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          <span className="whitespace-nowrap tabular-nums">
            {formatParisDateTime(new Date(value))}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    }),
    helper.accessor('error', {
      header: 'Message d’erreur',
      cell: (info) => <span className="block min-w-48 max-w-xl">{info.getValue() ?? '—'}</span>,
    }),
    helper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const line = info.row.original;
        return (
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              isLoading={retryMutation.isPending && retryMutation.variables?.includes(line.id)}
              onClick={() => {
                retryMutation.mutate([line.id]);
              }}
            >
              <RotateCcw aria-hidden="true" />
              Réessayer
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIgnoreTargets([line]);
              }}
            >
              <XCircle aria-hidden="true" />
              Ignorer
            </Button>
          </div>
        );
      },
    }),
  ]);

  const items = query.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        backLink={RUNS_BACK_LINK}
        title="Mises à jour Martelet non appliquées"
        description={
          <>
            Statuts et numéros de suivi envoyés par Martelet qui n’ont pas pu être reportés sur une commande (commande
            introuvable, erreur Shopify). Réessayez une fois la cause corrigée, ou ignorez-les si elles ont été traitées à
            la main. Pour écarter d’office des commandes créées hors Shopify (UGC, Toasty…), ajoutez une{' '}
            <Link to="/parametres" search={{ tab: 'martelet' }} className="font-medium text-primary underline-offset-4 hover:underline">
              règle « commandes à ignorer »
            </Link>
            .
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={items.length === 0}
              isLoading={retryMutation.isPending}
              onClick={() => {
                retryMutation.mutate(items.map((line) => line.id));
              }}
            >
              <RotateCcw aria-hidden="true" />
              Réessayer celles affichées ({items.length})
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={items.length === 0}
              onClick={() => {
                setIgnoreTargets(items);
              }}
            >
              <XCircle aria-hidden="true" />
              Ignorer celles affichées ({items.length})
            </Button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={items}
        getRowId={(row) => row.id}
        total={query.data?.total ?? 0}
        page={page}
        pageSize={50}
        sortBy={sortBy}
        sortDir={sortDir}
        sortableColumnIds={['fileName', 'orderNumber', 'store']}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={query.isPending}
        isError={query.isError}
        {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
        errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
        onRetry={() => {
          void query.refetch();
        }}
        toolbar={
          <form
            className="flex w-full flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters({ search: searchDraft.trim() || undefined });
            }}
          >
            <div className="relative w-full sm:w-80">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Commande, référence, suivi…"
                aria-label="Rechercher une mise à jour non appliquée"
                className="w-full pl-8"
                value={searchDraft}
                onChange={(event) => {
                  setSearchDraft(event.target.value);
                }}
              />
            </div>
            <Button type="submit" variant="outline">
              Rechercher
            </Button>
          </form>
        }
        emptyState={{
          title: 'Aucune mise à jour en attente',
          description: 'Toutes les mises à jour envoyées par Martelet ont été appliquées ou ignorées.',
        }}
      />

      <IgnoreReasonDialog
        open={ignoreTargets !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIgnoreTargets(null);
          }
        }}
        title={ignoreTargets && ignoreTargets.length > 1 ? `Ignorer ${String(ignoreTargets.length)} mises à jour` : 'Ignorer cette mise à jour'}
        description={
          ignoreTargets?.length === 1 && ignoreTargets[0]
            ? `La mise à jour Martelet de « ${ignoreTargets[0].orderNumber ?? ignoreTargets[0].reference ?? ignoreTargets[0].id} » sera marquée ignorée (traitée à la main).`
            : `Les ${String(ignoreTargets?.length ?? 0)} mises à jour affichées seront marquées ignorées (traitées à la main).`
        }
        isSubmitting={ignoreMutation.isPending}
        onConfirm={(reason) => {
          if (ignoreTargets) {
            ignoreMutation.mutate(
              { lineIds: ignoreTargets.map((line) => line.id), reason },
              {
                onSuccess: () => {
                  setIgnoreTargets(null);
                },
              },
            );
          }
        }}
      />
    </Page>
  );
}
